/**
 * Regras de tela da aba DRE e desvios P&L. O backend já entrega linhas, subtotais,
 * comparações e faixas; aqui só se escolhe o que mostrar para o mês selecionado.
 */
import type { DreComparacao, DreGrupoCusto, DreLinha, DreMes, DrePayload, FaixaOrcado } from '@/types/dashboards';

const nfMil = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/** Valor em R$ mil com sinal de menos tipográfico: `-10201234` → `−10.201`. */
export function fmtMil(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const mil = Math.round(v / 1000);
  if (mil === 0) return '0';
  return `${mil < 0 ? '−' : ''}${nfMil.format(Math.abs(mil))}`;
}

export function linhaDre(p: DrePayload, key: string): DreLinha | undefined {
  return p.linhas.find((l) => l.key === key);
}

export function mesDre(p: DrePayload, key: string | null): DreMes | undefined {
  return p.meses.find((m) => m.key === key);
}

/** Variação percentual sobre uma base com sinal (despesa negativa): `(v − base) / |base|`. */
export function variacaoPct(valor: number, base: number | null): number | null {
  if (base === null || base === 0) return null;
  return ((valor - base) / Math.abs(base)) * 100;
}

// ------------------------------------------------------------------ KPIs

export interface KpiDre {
  key: string;
  rotulo: string;
  icone: string;
  valor: number;
  margem: number | null;
  varMedia: number | null;
}

const KPIS: { key: string; rotulo: string; icone: string }[] = [
  { key: 'receita_liquida', rotulo: 'Receita líquida', icone: 'payments' },
  { key: 'lucro_bruto', rotulo: 'Lucro bruto', icone: 'account_balance_wallet' },
  { key: 'ebitda', rotulo: 'EBITDA', icone: 'query_stats' },
  { key: 'lucro_liquido', rotulo: 'Lucro líquido', icone: 'savings' },
];

export function kpisDoMes(p: DrePayload, mes: string): KpiDre[] {
  return KPIS.flatMap(({ key, rotulo, icone }) => {
    const l = linhaDre(p, key);
    if (!l || l.valores[mes] === undefined) return [];
    const comp = l.comparacao[mes];
    return [
      {
        key,
        rotulo,
        icone,
        valor: l.valores[mes],
        margem: key === 'receita_liquida' ? null : l.pct_rl[mes] ?? null,
        varMedia: variacaoPct(l.valores[mes], comp?.media_3m ?? null),
      },
    ];
  });
}

// ------------------------------------------------------------------ gráficos

export interface MetricaDre {
  key: string;
  rotulo: string;
}

export const METRICAS: MetricaDre[] = [
  { key: 'receita_liquida', rotulo: 'Receita líquida' },
  { key: 'lucro_bruto', rotulo: 'Lucro bruto' },
  { key: 'ebitda', rotulo: 'EBITDA' },
  { key: 'resultado_operacional', rotulo: 'Resultado operacional' },
  { key: 'lucro_liquido', rotulo: 'Lucro líquido' },
];

export interface PontoDre {
  key: string;
  label: string;
  valor: number;
  margem: number | null;
  incompleto: boolean;
}

export function serieMetrica(p: DrePayload, key: string): PontoDre[] {
  const l = linhaDre(p, key);
  if (!l) return [];
  return p.meses.map((m) => ({
    key: m.key,
    label: m.label,
    valor: l.valores[m.key] ?? 0,
    margem: l.pct_rl[m.key] ?? null,
    incompleto: m.contabil_incompleto,
  }));
}

/** Custos e despesas em % da receita líquida (o gráfico do Manus, com o contábil real). */
export const SERIES_CUSTO: MetricaDre[] = [
  { key: 'cpv', rotulo: 'Custo dos produtos vendidos' },
  { key: 'despesas_administrativas', rotulo: 'Despesas administrativas' },
  { key: 'despesas_comerciais', rotulo: 'Despesas comerciais' },
  { key: 'despesas_financeiras', rotulo: 'Despesas financeiras' },
];

export type PontoCusto = { key: string; label: string } & Record<string, number | string | null>;

export function seriePctCustos(p: DrePayload): PontoCusto[] {
  return p.meses.map((m) => {
    const ponto: PontoCusto = { key: m.key, label: m.label };
    for (const s of SERIES_CUSTO) {
      const pct = linhaDre(p, s.key)?.pct_rl[m.key];
      // Custo e despesa chegam negativos; no gráfico, a parte da receita que consomem.
      ponto[s.key] = pct === null || pct === undefined ? null : Math.abs(pct);
    }
    return ponto;
  });
}

// ------------------------------------------------------------------ desvios

export interface LinhaDesvio {
  key: string;
  rotulo: string;
  grupo: 'DRE' | 'Custo de produção';
  realizado: number;
  orcado: number;
  desvio: number;
  desvioPct: number | null;
  faixa: FaixaOrcado;
}

const PESO_FAIXA: Record<FaixaOrcado, number> = { critico: 0, atencao: 1, no_orcado: 2, melhor: 3 };
/** Abaixo de R$ 1 mil de orçado e de realizado a linha não diz nada: sai da tabela. */
const IRRELEVANTE = 1000;

function paraDesvio(
  item: { key: string; rotulo: string; valores: Record<string, number>; comparacao: Record<string, DreComparacao> },
  grupo: LinhaDesvio['grupo'],
  mes: string,
): LinhaDesvio | null {
  const c = item.comparacao[mes];
  if (!c || c.orcado === null || c.desvio_orcado === null || c.faixa === null) return null;
  if (Math.abs(c.orcado) < IRRELEVANTE && Math.abs(item.valores[mes] ?? 0) < IRRELEVANTE) return null;
  return {
    key: `${grupo}:${item.key}`,
    rotulo: item.rotulo,
    grupo,
    realizado: item.valores[mes],
    orcado: c.orcado,
    desvio: c.desvio_orcado,
    desvioPct: c.desvio_orcado_pct,
    faixa: c.faixa,
  };
}

/** Tudo o que tem orçado no mês, do desvio mais grave para o mais tranquilo. */
export function desviosDoMes(p: DrePayload, mes: string): LinhaDesvio[] {
  const dre = p.linhas.filter((l) => l.tem_orcado).map((l) => paraDesvio(l, 'DRE', mes));
  const custo = p.composicao_cpv.filter((g) => g.tem_orcado).map((g) => paraDesvio(g, 'Custo de produção', mes));
  return [...dre, ...custo]
    .filter((d): d is LinhaDesvio => d !== null)
    .sort((a, b) => PESO_FAIXA[a.faixa] - PESO_FAIXA[b.faixa] || a.desvio - b.desvio);
}

// ------------------------------------------------------------------ composição do custo

export interface LinhaComposicao {
  key: string;
  rotulo: string;
  valor: number;
  orcado: number | null;
  media3m: number | null;
  /** Peso no custo de produção do mês; a absorção e a baixa do estoque não têm. */
  pctDaProducao: number | null;
}

const FORA_DA_PRODUCAO = new Set(['absorcao', 'custo_vendas']);

function relevante(g: DreGrupoCusto, mes: string): boolean {
  const c = g.comparacao[mes];
  return [g.valores[mes], c?.orcado, c?.media_3m].some((v) => Math.abs(v ?? 0) >= IRRELEVANTE);
}

export function composicaoDoMes(p: DrePayload, mes: string): { linhas: LinhaComposicao[]; cpv: number } {
  const cpv = linhaDre(p, 'cpv')?.valores[mes] ?? 0;
  const producao = p.composicao_cpv
    .filter((g) => !FORA_DA_PRODUCAO.has(g.key))
    .reduce((s, g) => s + (g.valores[mes] ?? 0), 0);
  const linhas = p.composicao_cpv
    .filter((g) => relevante(g, mes))
    .map((g: DreGrupoCusto) => ({
      key: g.key,
      rotulo: g.rotulo,
      valor: g.valores[mes] ?? 0,
      orcado: g.comparacao[mes]?.orcado ?? null,
      media3m: g.comparacao[mes]?.media_3m ?? null,
      pctDaProducao: FORA_DA_PRODUCAO.has(g.key) || !producao ? null : ((g.valores[mes] ?? 0) / producao) * 100,
    }));
  return { linhas, cpv };
}
